import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LayoutDashboard, Store, Briefcase, Bell, MessageSquare, Settings as SettingsIcon,
  Menu, LogOut, ExternalLink, Loader2, ListChecks,
} from "lucide-react";
import { getMe, clearAuth, MOCK_BUSINESS_API } from "@/business/api";

const NAV = [
  { to: "/business/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "sidebar-nav-dashboard-link" },
  { to: "/business/dashboard/venue", label: "My Venues", icon: Store, testId: "sidebar-nav-venue-link" },
  { to: "/business/dashboard/hire", label: "Hire a Busker", icon: Briefcase, testId: "sidebar-nav-hire-link" },
  { to: "/business/dashboard/gigs", label: "My Gigs", icon: ListChecks, testId: "sidebar-nav-gigs-link" },
  { to: "/business/dashboard/notifications", label: "Notifications", icon: Bell, testId: "sidebar-nav-notifications-link" },
  { to: "/business/dashboard/messages", label: "Messages", icon: MessageSquare, testId: "sidebar-nav-messages-link" },
  { to: "/business/dashboard/settings", label: "Settings", icon: SettingsIcon, testId: "sidebar-nav-settings-link" },
];

function SidebarContent({ user, onLogout, onNavigate }) {
  return (
    <div className="flex h-full flex-col bg-[#0F1115] text-white">
      <div className="h-14 flex items-center gap-2 px-4 border-b border-white/10">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF6A00] text-white font-bold">B</span>
        <span className="font-semibold tracking-tight">BuskerBeat <span className="text-[#FF6A00]">Business</span></span>
      </div>
      {user && (() => {
        // Resolve the best public-profile slug:
        //   1. If /me returned a `locations` array, prefer the primary
        //      venue's slug.
        //   2. Else fall back to `user.username` (legacy single-venue
        //      shape) — but ONLY under /business/, never /busker/.
        const primaryVenue = Array.isArray(user.locations)
          ? user.locations.find((v) => v && v.is_primary) || user.locations[0]
          : null;
        const publicSlug =
          (primaryVenue && (primaryVenue.slug || primaryVenue.username)) ||
          user.username ||
          "";
        const publicHref = publicSlug ? `/business/${publicSlug}` : null;
        return (
          <div className="px-4 py-4 border-b border-white/10" data-testid="sidebar-venue-card">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center text-sm font-semibold text-white/80">
                {(user.business_name || "V").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" data-testid="sidebar-venue-name">{user.business_name || "Your Venue"}</div>
              </div>
            </div>
            {publicHref ? (
              <a href={publicHref} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#FF6A00] hover:underline" data-testid="sidebar-view-public-profile">
                View Public Profile <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-white/40" data-testid="sidebar-view-public-profile-disabled">
                Public profile pending
              </span>
            )}
          </div>
        );
      })()}
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
        {NAV.map(({ to, label, icon: Icon, end, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={testId}
            className={({ isActive }) =>
              `relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150 ` +
              (isActive
                ? "bg-white/10 text-white ring-1 ring-inset ring-[#FF6A00]/40 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-[#FF6A00]"
                : "text-white/75 hover:text-white hover:bg-white/5")
            }
          >
            <Icon className="h-4 w-4 opacity-90" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <button onClick={onLogout} data-testid="sidebar-logout-button" className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors duration-150">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSheet, setOpenSheet] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMe();
        if (!cancelled) { setUser(me); setLoading(false); }
      } catch {
        if (!cancelled) navigate("/business/login", { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = () => {
    clearAuth();
    toast.success("Signed out.");
    navigate("/business/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F8]" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF6A00]" />
      </div>
    );
  }

  const pageTitle = (() => {
    const seg = location.pathname.replace(/\/$/, "").split("/").pop();
    const map = { dashboard: "Dashboard", venue: "My Venues", hire: "Hire a Busker", gigs: "My Gigs", notifications: "Notifications", messages: "Messages", settings: "Settings" };
    return map[seg] || "Dashboard";
  })();

  return (
    <div className="min-h-screen bg-[#F7F7F8] text-[#111827]" data-testid="dashboard-shell">
      <div className="grid lg:grid-cols-[280px_1fr] min-h-screen">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:block lg:sticky lg:top-0 lg:h-screen">
          <SidebarContent user={user} onLogout={onLogout} />
        </aside>
        {/* Main */}
        <div className="flex min-h-screen flex-col">
          {/* Top header */}
          <header className="sticky top-0 z-30 h-14 px-4 sm:px-6 flex items-center justify-between gap-2 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2 min-w-0">
              <Sheet open={openSheet} onOpenChange={setOpenSheet}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="dashboard-open-sidebar-button" aria-label="Open menu"><Menu className="h-5 w-5" /></Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-[280px]" data-testid="dashboard-sidebar-sheet">
                  <SidebarContent user={user} onLogout={onLogout} onNavigate={() => setOpenSheet(false)} />
                </SheetContent>
              </Sheet>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" data-testid="dashboard-header-title">{pageTitle}</div>
                <div className="text-[11px] text-[#6B7280] truncate hidden sm:block">Welcome back, {user?.business_name || "venue owner"}.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {MOCK_BUSINESS_API && <span className="hidden sm:inline-flex items-center rounded-full bg-[#FFF1E8] text-[#B54708] border border-[#FFD7BF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="dashboard-mock-badge">Preview</span>}
              <Button size="sm" onClick={() => navigate("/business/dashboard/hire")} data-testid="dashboard-post-gig-button" className="bg-[#FF6A00] hover:bg-[#E85F00] text-white">
                Post a Gig
              </Button>
            </div>
          </header>
          <main className="flex-1 px-4 py-5 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-[1200px]">
              <Outlet context={{ user, setUser }} />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
